import ModalViewModel from 'infra/ModalViewModel'
import { MessageBankStore } from '../MessageBankStore'
import { MessageBankReply } from '../MessageBankReply'
import { action, computed, observable, makeObservable } from 'mobx'
import { task, TaskGroup } from 'mobx-task'
import { Id } from 'types/definitions'

export type MessageBankDialogViewModelFactory = (
  getWorkspaceId: () => string,
  onReplySelected: (replyContent: string) => void,
) => MessageBankDialogViewModel

/**
 * MessageBank Dialog view model.
 */
export default class MessageBankDialogViewModel extends ModalViewModel {
  /**
   * Task group for fetching and saving.
   */
  tasks: TaskGroup<any, any>

  @observable private _selectedReply: MessageBankReply | null = null

  constructor(
    private getWorkspaceId: () => string,
    private onMessageBankReplySelected: (replyContent: string) => void,
    private messageBankStore: MessageBankStore,
  ) {
    super()
    makeObservable(this)
    this.tasks = TaskGroup<any, any>([this._fetchBankReplies])
    this.show$.subscribe(this._fetchBankReplies)
  }

  @computed
  get workspaceId() {
    return this.getWorkspaceId()
  }

  @computed
  get selectedReply() {
    return this._selectedReply || this.messageBankReplies[0] || null
  }

  @computed
  get messageBankReplies(): MessageBankReply[] {
    if (this.messageBank) {
      return this.messageBank.replies.items
    }

    return []
  }

  /**
   * The message bank for the job's workspace.
   */
  @computed
  get messageBank() {
    return this.messageBankStore.getForWorkspace(this.workspaceId)
  }

  @computed
  get messageBankHasReplies() {
    return this.messageBank && this.messageBank.replies.length > 0
  }

  @action
  setSelectedReply(reply: MessageBankReply) {
    this._selectedReply = reply
  }

  @action.bound
  useSelectedReply() {
    this.onMessageBankReplySelected(this.selectedReply.content)
    super.close()
  }

  /**
   * Fetches messagebank and corresponding replies.
   */
  private _fetchBankReplies = task(() => this.fetchMessageBankReplies())

  @action
  private async fetchMessageBankReplies() {
    const messageBank = await this.getMessageBankForWorkspace()

    if (messageBank) {
      await this.messageBankStore
        .fetchMessageBankReplies(Id.assert(messageBank.id))
        .catch((err) => {
          console.log(`Couldn't find message bank for ${this.workspaceId}`, err)
        })
    }
  }

  async getMessageBankForWorkspace() {
    const messageBank = await this.messageBankStore.fetchWorkspaceMessageBank(
      this.workspaceId,
    )
    return messageBank
  }
}
